/**
 * The frame the four Call Report pages share.
 *
 * Each of them is the same screen — toolbar, Table ⇄ Analytics tabs, paging,
 * export, filter drawer — differing only in which endpoint it calls, which
 * columns it shows, which filters it offers and which analytics panel it
 * renders. Writing that four times is how the fourth report quietly ends up
 * without a Reset button.
 *
 * So each page file declares its report and hands it here; everything
 * structural lives in one place, exactly as `ReportShell` already does for the
 * conversation reports.
 */
import { useMemo, useState, type ComponentType, type JSX, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import CallReportFilters, {
  type CallFilterField,
} from '../../../components/reports/calls/CallReportFilters.js'
import {
  columnView,
  readCallCell,
} from '../../../components/reports/calls/columns.js'
import type { ReportColumnDef } from '../../../components/reports/agent-performance/columns.js'
import ReportDataTable from '../../../components/reports/shared/ReportDataTable.js'
import ReportShell from '../../../components/reports/shared/ReportShell.js'
import {
  useCallReport,
  type CallReportFilters as Filters,
} from '../../../hooks/useCallReport.js'
import { useReportView } from '../../../hooks/useReportView.js'

export interface CallReportPageProps {
  Icon: LucideIcon
  title: string
  /** Shown under the title before the report has been run. */
  intro: string
  /** `calls` / `selections` / `intervals` — what the row count counts. */
  unit: string
  /** API path, e.g. `/reports/calls/history`. */
  path: string
  /** Export registry id, e.g. `call.history`. */
  reportId: string
  /**
   * Saved column-view key, when it must differ from `reportId` — bump it
   * after a layout redesign so stale saved views do not hide new columns.
   */
  viewId?: string
  catalog: ReportColumnDef[]
  fields: readonly CallFilterField[]
  windowNote: string
  defaultInterval?: string
  searchPlaceholder: string
  renderAnalytics: (rows: Record<string, unknown>[]) => ReactNode
  emptyDescription: string
  noResultsTitle: string
  noResultsDescription: string
  /** Optional detail drawer that opens when a row is clicked. */
  DetailDrawer?: ComponentType<{ row: Record<string, unknown>; onClose: () => void }>
  /**
   * Extra toolbar controls. Pass a function to get a `refresh` that re-runs
   * the report with the filters it was last run with (a no-op before the
   * first run).
   */
  headerActions?: ReactNode | ((refresh: () => void) => ReactNode)
}

export default function CallReportPage({
  Icon,
  title,
  intro,
  unit,
  path,
  reportId,
  viewId,
  catalog,
  fields,
  windowNote,
  defaultInterval,
  searchPlaceholder,
  renderAnalytics,
  emptyDescription,
  noResultsTitle,
  noResultsDescription,
  DetailDrawer,
  headerActions,
}: CallReportPageProps): JSX.Element {
  const { rows, total, loading, error, hasRun, run, reset } = useCallReport(path)
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [ranWith, setRanWith] = useState<Filters | null>(null)
  const [selectedRow, setSelectedRow] = useState<Record<string, unknown> | null>(null)

  // The catalog is a module constant, so this derives once per report rather
  // than rebuilding a 40-entry key list on every keystroke in the search box.
  const { defaults, sanitize } = useMemo(() => columnView(catalog), [catalog])
  const { columns, setColumns, setColumnsLocal, commit } = useReportView(
    viewId ?? reportId,
    defaults,
    sanitize,
  )

  const handleRun = (filters: Filters): void => {
    setRanWith(filters)
    setFiltersOpen(false)
    void run(filters)
  }

  const toggleColumn = (key: string): void => {
    if (catalog.find((c) => c.key === key)?.locked) return
    const next = columns.includes(key)
      ? columns.filter((k) => k !== key)
      // Re-inserted at its catalog position rather than appended, so turning a
      // column back on does not silently reorder the table.
      : [...columns, key].sort((a, b) => defaults.indexOf(a) - defaults.indexOf(b))
    setColumns(next)
  }

  const reorder = (from: string, to: string): void => {
    if (from === to) return
    setColumnsLocal((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(from)
      const toIdx = next.indexOf(to)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(toIdx, 0, ...next.splice(fromIdx, 1))
      return next
    })
  }

  const subtitle = ranWith
    ? `${total} ${unit}${total === 1 ? '' : 's'}`
      + ` · ${ranWith.startDate} ${ranWith.startTime} → ${ranWith.endDate} ${ranWith.endTime}`
      + ` · ${ranWith.timezone}`
    : intro

  return (
    <>
      <ReportShell<Record<string, unknown>>
      Icon={Icon}
      title={title}
      subtitle={subtitle}
      hasRun={hasRun}
      ranWith={ranWith}
      loading={loading}
      error={error}
      rows={rows}
      catalog={catalog}
      visibleColumns={columns}
      onToggleColumn={toggleColumn}
      onReorderColumn={reorder}
      onCommitColumns={commit}
      searchPlaceholder={searchPlaceholder}
      readCell={readCallCell}
      reportId={reportId}
      reportName={title}
      // The export must reproduce exactly the report on screen, so it is sent
      // the window that was RUN — not whatever the drawer currently holds.
      exportParams={ranWith && {
        timezone: ranWith.timezone,
        interval: ranWith.interval,
        startDate: ranWith.startDate,
        endDate: ranWith.endDate,
        startTime: ranWith.startTime,
        endTime: ranWith.endTime,
        ...ranWith.extra,
      }}
      onOpenFilters={() => setFiltersOpen(true)}
      headerActions={typeof headerActions === 'function'
        ? headerActions(() => { if (ranWith) void run(ranWith) })
        : headerActions}
      renderTable={(pageRows, visibleColumns) => (
        <ReportDataTable
          rows={pageRows}
          columns={catalog}
          visibleColumns={visibleColumns}
          {...(DetailDrawer ? { onRowClick: (row: Record<string, unknown>) => setSelectedRow(row) } : {})}
        />
      )}
      renderAnalytics={(visible) => renderAnalytics(visible)}
      emptyTitle="No report yet"
      emptyDescription={emptyDescription}
      noResultsTitle={noResultsTitle}
      noResultsDescription={noResultsDescription}
    >
      <CallReportFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onRun={handleRun}
        onReset={() => { setRanWith(null); reset() }}
        running={loading}
        fields={fields}
        windowNote={windowNote}
        {...(defaultInterval !== undefined ? { defaultInterval } : {})}
      />
    </ReportShell>

    {DetailDrawer && selectedRow && (
      <DetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />
    )}
  </>
  )
}
